import { lazy } from 'react'

const RECOVERABLE_LAZY_IMPORT_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /failed to load module script/i,
  /dynamically imported module/i,
]

function buildReloadStorageKey(cacheKey) {
  return `sofera:lazy-reload:${cacheKey || 'global'}`
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function clearReloadFlag(cacheKey) {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(buildReloadStorageKey(cacheKey))
    }
  } catch {}
}

function hasReloaded(cacheKey) {
  try {
    return typeof window !== 'undefined' && window.sessionStorage?.getItem(buildReloadStorageKey(cacheKey)) === '1'
  } catch {
    return false
  }
}

function markReload(cacheKey) {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem(buildReloadStorageKey(cacheKey), '1')
    }
  } catch {}
}

export function isRecoverableLazyImportError(error) {
  const message = String(error?.message || error || '')
  return RECOVERABLE_LAZY_IMPORT_PATTERNS.some((pattern) => pattern.test(message))
}

export async function loadLazyModuleWithRetry(
  loader,
  {
    cacheKey,
    retries = 1,
    retryDelayMs = 250,
    reloadOnChunkError = true,
    onReload = () => window.location.reload(),
  } = {},
) {
  let lastError = null

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      if (attempt > 0 && retryDelayMs > 0) {
        await sleep(retryDelayMs)
      }

      const module = await loader()
      clearReloadFlag(cacheKey)
      return module
    } catch (error) {
      lastError = error
    }
  }

  if (!isRecoverableLazyImportError(lastError) || !reloadOnChunkError || typeof window === 'undefined') {
    throw lastError
  }

  if (!hasReloaded(cacheKey)) {
    markReload(cacheKey)
    onReload()
    return new Promise(() => {})
  }

  clearReloadFlag(cacheKey)
  throw lastError
}

export function lazyWithRetry(loader, options) {
  return lazy(() => loadLazyModuleWithRetry(loader, options))
}
